import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";
import { createTriggerRule } from "../utils/defaults";

const EMPTY_META = {
  stages: [],
  keyboards: [],
  frequencyModes: []
};

const EMPTY_CONFIG = {
  version: 1,
  updatedAt: null,
  triggers: []
};

function serializeConfig(config) {
  try {
    return JSON.stringify(config || EMPTY_CONFIG);
  } catch (_) {
    return JSON.stringify(EMPTY_CONFIG);
  }
}

export function useAdminTriggers({ adminToken, enabled }) {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [savedConfigSnapshot, setSavedConfigSnapshot] = useState(() => serializeConfig(EMPTY_CONFIG));
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedTriggerId, setSelectedTriggerId] = useState("");

  const selectFirstIfNeeded = useCallback((nextConfig) => {
    const list = Array.isArray(nextConfig?.triggers) ? nextConfig.triggers : [];
    setSelectedTriggerId((prev) => {
      if (prev && list.some((item) => item.id === prev)) return prev;
      return list[0]?.id || "";
    });
  }, []);

  const fetchConfig = useCallback(async (token = adminToken) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await adminApi.getTriggersConfig(token);
      const nextConfig = res.data?.config || EMPTY_CONFIG;
      setConfig(nextConfig);
      setSavedConfigSnapshot(serializeConfig(nextConfig));
      setMeta(res.data?.meta || EMPTY_META);
      selectFirstIfNeeded(nextConfig);
      setResult(null);
    } catch (error) {
      console.error("Failed to load triggers:", error);
      setResult({ success: false, message: "Не удалось загрузить триггеры." });
    } finally {
      setLoading(false);
    }
  }, [adminToken, selectFirstIfNeeded]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    fetchConfig(adminToken);
  }, [enabled, adminToken, fetchConfig]);

  const updateTrigger = useCallback((triggerId, updater) => {
    setConfig((prev) => ({
      ...prev,
      triggers: (prev.triggers || []).map((item) => {
        if (item.id !== triggerId) return item;
        if (typeof updater === "function") return updater(item);
        return { ...item, ...(updater || {}) };
      })
    }));
  }, []);

  const addTrigger = useCallback((stage = "user_inactive") => {
    const next = createTriggerRule(stage);
    setConfig((prev) => ({
      ...prev,
      triggers: [...(prev.triggers || []), next]
    }));
    setSelectedTriggerId(next.id);
    setResult(null);
    return next.id;
  }, []);

  const duplicateTrigger = useCallback((triggerId) => {
    let createdId = "";
    setConfig((prev) => {
      const current = (prev.triggers || []).find((item) => item.id === triggerId);
      if (!current) return prev;
      const copy = {
        ...JSON.parse(JSON.stringify(current)),
        id: createTriggerRule(current.stage).id,
        title: `${current.title} (copy)`
      };
      createdId = copy.id;
      setSelectedTriggerId(copy.id);
      return {
        ...prev,
        triggers: [...(prev.triggers || []), copy]
      };
    });
    setResult(null);
    return createdId;
  }, []);

  const removeTrigger = useCallback((triggerId) => {
    let nextSelected = "";
    setConfig((prev) => {
      const nextTriggers = (prev.triggers || []).filter((item) => item.id !== triggerId);
      nextSelected = nextTriggers[0]?.id || "";
      setSelectedTriggerId(nextSelected);
      return {
        ...prev,
        triggers: nextTriggers
      };
    });
    setResult(null);
    return nextSelected;
  }, []);

  const save = useCallback(async () => {
    if (!adminToken) return false;
    setSaving(true);
    setResult(null);
    try {
      const res = await adminApi.saveTriggersConfig(adminToken, config);
      const nextConfig = res.data?.config || config;
      setConfig(nextConfig);
      setSavedConfigSnapshot(serializeConfig(nextConfig));
      setMeta(res.data?.meta || meta);
      selectFirstIfNeeded(nextConfig);
      setResult({ success: true, message: "Триггеры сохранены." });
      return true;
    } catch (error) {
      console.error("Failed to save triggers:", error);
      setResult({ success: false, message: "Не удалось сохранить триггеры." });
      return false;
    } finally {
      setSaving(false);
    }
  }, [adminToken, config, meta, selectFirstIfNeeded]);

  const resetToDefaults = useCallback(async () => {
    if (!adminToken) return false;
    setSaving(true);
    setResult(null);
    try {
      const res = await adminApi.resetTriggersConfig(adminToken);
      const nextConfig = res.data?.config || EMPTY_CONFIG;
      setConfig(nextConfig);
      setSavedConfigSnapshot(serializeConfig(nextConfig));
      setMeta(res.data?.meta || EMPTY_META);
      selectFirstIfNeeded(nextConfig);
      setResult({ success: true, message: "Триггеры по умолчанию восстановлены." });
      return true;
    } catch (error) {
      console.error("Failed to reset triggers:", error);
      setResult({ success: false, message: "Не удалось восстановить значения по умолчанию." });
      return false;
    } finally {
      setSaving(false);
    }
  }, [adminToken, selectFirstIfNeeded]);

  const selectedTrigger = useMemo(
    () => (config.triggers || []).find((item) => item.id === selectedTriggerId) || null,
    [config.triggers, selectedTriggerId]
  );

  const dirty = useMemo(() => serializeConfig(config) !== savedConfigSnapshot, [config, savedConfigSnapshot]);

  return {
    state: {
      config,
      meta,
      loading,
      saving,
      dirty,
      result,
      selectedTriggerId,
      selectedTrigger
    },
    actions: {
      reload: () => fetchConfig(adminToken),
      save,
      resetToDefaults,
      setSelectedTriggerId,
      updateTrigger,
      addTrigger,
      duplicateTrigger,
      removeTrigger
    }
  };
}

export default useAdminTriggers;
